import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import { formatProduct, formatPriceWithPrecision } from '../utils/utilfunction';
import { formatTimeToIST, formatOrderUpdateTime } from '../utils/dateUtils';
import ExchangeTag from '../components/Common/ExchangeTag';

// Status color configuration
const statusColorConfig = {
  PENDING: 'warning.main',
  COMPLETE: 'success.main',
  REJECTED: 'error.main',
  CONFIRMED: 'success.main',
  CANCELLED: 'error.main',
  EXCHANGE_RESPONSE_PENDING: 'warning.main',
  CANCEL_CONFIRMED: 'error.main',
  default: 'text.secondary'
};

const getStatusColor = (status) => statusColorConfig[status] || statusColorConfig.default;

// Helper function to get exchange display name
const getExchangeDisplayName = (item) => {
  const { exchange, category, series } = item;

  switch (exchange) {
    case 'NFO':
      return 'Derivatives';
    case 'MCX':
      return 'Commodities';
    case 'NSE':
      return series && series.toLowerCase() === 'indices' ? 'Indices' : 'Stock'
    case 'CDS':
      return category === 'USSTOCK' ? category : 'FOREX PAIR';
    case 'DCXS':
    case 'DCSX':
      return category || 'Crypto';
    default:
      return exchange || 'Unknown';
  }
};

const createSymbolCell = (item) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 'medium',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        flex: 1,
        minWidth: 0
      }}
    >
      {item.trading_symbol}
    </Typography>
    <ExchangeTag exchange={item.exchange} />
  </Box>
);

const createSideCell = (item, theme) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
    <Typography
      sx={{
        color: item.order_side === 'BUY' ? 'success.main' : 'error.main',
        fontWeight: 'bold'
      }}
    >
      {item.order_side}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {formatProduct(item.product)}
    </Typography>
  </Box>
);

export const pendingOrdersConfig = (handleModifyOrder, handleDeleteClick, isMobile) => [
  {
    field: 'symbol',
    headerName: 'Symbol',
    width: isMobile ? '25%' : '20%',
    render: createSymbolCell
  },
  {
    field: 'side',
    headerName: 'Side',
    width: isMobile ? '18%' : '15%',
    render: createSideCell
  },
  {
    field: 'order_type',
    headerName: 'Type',
    width: isMobile ? '12%' : '12%',
    align: 'right',
    render: (item) => `${item.order_type}`
  },
  {
    field: 'quantity',
    headerName: 'Qty',
    width: isMobile ? '12%' : '12%',
    align: 'right',
    render: (item) => `${item.filled_quantity}/${item.quantity}`
  },
  {
    field: 'price',
    headerName: 'Price',
    width: isMobile ? '15%' : '13%',
    align: 'right',
    render: (item) => {
      // Apply price precision formatting for CDS exchange
      if ((item.exchange === 'CDS' || item.exchange === "DCSX") && item.price_precision !== undefined) {
        return `₹${formatPriceWithPrecision(item.price, item.price_precision, {
          showCurrency: false,
          showCommas: false,
          removeTrailingZeros: false
        })}`;
      }
      return `₹${item.price.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    }
  },
  {
    field: 'order_entry_time',
    headerName: 'Time',
    width: '12%',
    hideOnMobile: true,
    format: (value) => formatTimeToIST(value)
  },
  {
    field: 'order_status',
    headerName: 'Status',
    width: isMobile ? '15%' : '15%',
    render: (item) => {
      const isRejected = item.order_status === 'REJECTED' && item.rejection_reason && item.rejection_reason !== 'NONE';
      const statusContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{
            color: getStatusColor(item.order_status),
            fontWeight: 'medium',
            fontSize: isMobile ? '0.65rem' : '0.75rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {item.order_status}
          </Box>
          {isRejected && (
            <Typography
              variant="caption"
              color="error.main"
              sx={{
                fontSize: isMobile ? '0.55rem' : '0.65rem',
                lineHeight: 1.1,
                display: '-webkit-box',
                WebkitLineClamp: isMobile ? 1 : 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                mt: 0.25
              }}
            >
              Rejection Reason: {item.rejection_reason}
            </Typography>
          )}
        </Box>
      );
      
      return isRejected && !isMobile ? (
        <Tooltip title={item.rejection_reason} arrow placement="top-start">
          {statusContent}
        </Tooltip>
      ) : statusContent;
    }
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: isMobile ? '15%' : '13%',
    render: (item) => (
      <Box sx={{ display: 'flex', gap: isMobile ? 1 : 1.5, justifyContent: 'flex-start' }}>
        <Tooltip title="Modify Order" arrow>
          <IconButton
            size="small"
            onClick={(e) => handleModifyOrder(item, e)}
            disabled={item.order_status === "EXCHANGE_RESPONSE_PENDING"}
            sx={{
              p: isMobile ? "4px" : "6px",
              color: "primary.main",
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.04)",
              },
            }}
          >
            <EditIcon sx={{ fontSize: isMobile ? "1.1rem" : "1.3rem" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Cancel Order" arrow>
          <IconButton
            size="small"
            onClick={(e) => handleDeleteClick(item, e)}
            disabled={item.order_status === "EXCHANGE_RESPONSE_PENDING"}
            sx={{
              p: isMobile ? "4px" : "6px",
              color: "error.main",
              "&:hover": {
                backgroundColor: "rgba(211, 47, 47, 0.04)",
              },
            }}
          >
            <CancelIcon sx={{ fontSize: isMobile ? "1.1rem" : "1.3rem" }} />
          </IconButton>
        </Tooltip>
      </Box>
    )
  }
];

export const completedOrdersConfig = (isMobile) => [
  {
    field: 'symbol',
    headerName: 'Symbol',
    width: isMobile ? '25%' : '20%',
    render: createSymbolCell
  },
  {
    field: 'side',
    headerName: 'Side',
    width: isMobile ? '18%' : '15%',
    render: createSideCell
  },
  {
    field: 'order_type',
    headerName: 'Type',
    width: isMobile ? '12%' : '12%',
    align: 'right',
    render: (item) => `${item.order_type}`
  },
  {
    field: 'quantity',
    headerName: 'Qty',
    width: isMobile ? '12%' : '12%',
    align: 'right',
    render: (item) => `${item.filled_quantity}/${item.quantity}`
  },
  {
    field: 'average_price',
    headerName: 'Price',
    width: isMobile ? '15%' : '13%',
    align: 'right',
    render: (item) => {
      // Apply price precision formatting for CDS exchange
      if ((item.exchange === 'CDS' || item.exchange === "DCSX") && item.price_precision !== undefined) {
        return `₹${formatPriceWithPrecision(item.average_price, item.price_precision, {
          showCurrency: false,
          showCommas: false,
          removeTrailingZeros: false
        })}`;
      }
      return `₹${item.average_price.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    }
  },
  {
    field: 'order_status',
    headerName: 'Status',
    width: isMobile ? '15%' : '15%',
    render: (item) => {
      const isRejected = item.order_status === 'REJECTED' && item.rejection_reason && item.rejection_reason !== 'NONE';
      const statusContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{
            color: getStatusColor(item.order_status),
            fontWeight: 'medium',
            fontSize: isMobile ? '0.65rem' : '0.75rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {item.order_status}
          </Box>
          {isRejected && (
            <Typography
              variant="caption"
              color="error.main"
              sx={{
                fontSize: isMobile ? '0.55rem' : '0.65rem',
                lineHeight: 1.1,
                display: '-webkit-box',
                WebkitLineClamp: isMobile ? 1 : 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                mt: 0.25
              }}
            >
              Rejection Reason: {item.rejection_reason}
            </Typography>
          )}
        </Box>
      );
      
      return isRejected && !isMobile ? (
        <Tooltip title={item.rejection_reason} arrow placement="top-start">
          {statusContent}
        </Tooltip>
      ) : statusContent;
    }
  },
  {
    field: 'last_activity_reference',
    headerName: 'Time',
    width: '12%',
    hideOnMobile: true,
    render: (item) => formatOrderUpdateTime(item, false)
  },
];

export const tradesConfig = (isMobile) => [
  {
    field: 'symbol',
    headerName: 'Symbol',
    width: isMobile ? '30%' : '25%',
    render: createSymbolCell
  },
  {
    field: 'side',
    headerName: 'Side',
    width: isMobile ? '20%' : '15%',
    render: createSideCell
  },
  {
    field: 'trade_quantity',
    headerName: 'Qty',
    width: isMobile ? '25%' : '15%',
    align: 'right'
  },
  {
    field: 'trade_price',
    headerName: 'Price',
    width: isMobile ? '25%' : '15%',
    align: 'right',
    render: (item) => {
      // Apply price precision formatting for CDS exchange
      if ((item.exchange === 'CDS' || item.exchange === "DCSX") && item.price_precision !== undefined) {
        return `₹${formatPriceWithPrecision(item.trade_price, item.price_precision, {
          showCurrency: false,
          showCommas: false,
          removeTrailingZeros: false
        })}`;
      }
      return `₹${item.trade_price.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    }
  },
  {
    field: 'last_activity_reference',
    headerName: 'Time',
    width: '12%',
    hideOnMobile: true,
    render: (item) => formatOrderUpdateTime(item, true)
  },
  // {
  //   field: 'trade_status',
  //   headerName: 'Status',
  //   width: isMobile ? '15%' : '15%',
  //   render: (item) => (
  //     <Box sx={{ 
  //       color: getStatusColor(item.trade_status),
  //       fontWeight: 'medium',
  //       fontSize: isMobile ? '0.65rem' : '0.75rem',
  //       whiteSpace: 'nowrap',
  //       overflow: 'hidden',
  //       textOverflow: 'ellipsis'
  //     }}>
  //       {item.trade_status}
  //     </Box>
  //   )
  // }
];

// Position quantity and price color config
const quantityColorConfig = {
  positive: 'success.main',
  negative: 'error.main',
  neutral: 'text.primary'
};

const getQuantityColor = (value) => {
  if (value > 0) return quantityColorConfig.positive;
  if (value < 0) return quantityColorConfig.negative;
  return quantityColorConfig.neutral;
};

export const positionsConfig = (isMobile, exchangeSpecificStringifiedNumber) => {
  // Helper function to format prices with CDS precision support
  const formatPositionPrice = (item, price) => {
    if ((item.exchange === 'CDS' || item.exchange === "DCSX") && item.price_precision !== undefined) {
      return formatPriceWithPrecision(price, item.price_precision, {
        showCurrency: false,
        showCommas: false,
        removeTrailingZeros: false
      });
    }
    return exchangeSpecificStringifiedNumber(item.exchange, price);
  };

  return [
    {
      field: 'symbol',
      headerName: 'Symbol',
      width: '200px',
      render: createSymbolCell
    },
    {
      field: 'product',
      headerName: 'Product',
      width: '100px',
      format: formatProduct
    },
    {
      field: 'net_quantity',
      headerName: 'Qty',
      width: '80px',
      align: 'right',
      render: (item) => (
        <Box sx={{
          color: getQuantityColor(item.net_quantity),
          fontWeight: 500
        }}>
          {item.net_quantity}
        </Box>
      )
    },
    {
      field: 'buyAverage',
      headerName: 'Buy Avg',
      width: '100px',
      align: 'right',
      render: (item) => formatPositionPrice(item, item.buyAverage)
    },
    {
      field: 'sellAverage',
      headerName: 'Sell Avg',
      width: '100px',
      align: 'right',
      render: (item) => formatPositionPrice(item, item.sellAverage)
    },
    {
      field: 'blocked_margin',
      headerName: "Blocked Margin",
      width: '110px',
      align: 'right',
      render: (item) => formatPositionPrice(item, item.blocked_margin)
    },
    {
      field: 'ltp',
      headerName: 'Price',
      width: '100px',
      align: 'right',
      render: (item) => (
        <Box sx={{
          color: getQuantityColor(item.net_quantity),
          fontWeight: 500
        }}>
          {formatPositionPrice(item, item.ltp)}
        </Box>
      )
    },
    {
      field: 'pnl',
      headerName: 'P&L',
      width: '120px',
      align: 'right',
      render: (item) => (
        <Box sx={{ textAlign: 'right' }}>
          <Box sx={{
            color: getQuantityColor(item.pnl),
            fontWeight: 500,
            lineHeight: 1.1
          }}>
            ₹{formatPositionPrice(item, Math.abs(item.pnl))}
          </Box>
          <Box
            sx={{
              mt: 0.25,
              fontSize: '0.7rem',
              color: getQuantityColor(item.pnl),
              lineHeight: 1
            }}
          >
            {(Number.isFinite(item.pnl_percent_change) ? item.pnl_percent_change : 0).toFixed(2)}%
          </Box>
        </Box>
      )
    },
    // {
    //   field: 'actions',
    //   headerName: '',
    //   width: '80px',
    //   render: (item) => (
    //     <Box 
    //       sx={{ 
    //         opacity: 0,
    //         transition: 'opacity 0.2s',
    //         '.MuiTableRow-root:hover &': {
    //           opacity: 1
    //         }
    //       }}
    //     >
    //       <Button
    //         variant="outlined"
    //         size="small"
    //         sx={{
    //           minWidth: 'auto',
    //           px: 1,
    //           py: 0.5,
    //           fontSize: '0.75rem'
    //         }}
    //         onClick={(e) => {
    //           e.stopPropagation();
    //           item.onConvert?.(item);
    //         }}
    //       >
    //         Convert
    //       </Button>
    //     </Box>
    //   )
    // }
  ];
};